import { v4 as uuidv4 } from "uuid";
import i18n from "../../../Localization/i18next";

import {
  buildNutritionEquivalenceItemSnapshot,
  buildNutritionFoodItemSnapshot,
  inflateNutritionRecipeRecord,
} from "../../Firestore/nutrition/nutritionRecords";
import {
  buildIngredientFromEquivalentSelection,
  buildIngredientsFromRecipeSelection,
  buildIngredientFromSelectedFood,
  cloneIngredient,
  getRecipeTotals,
  getSafeNumber,
  normalizeDecimalInput,
  parsePositiveNumber,
  sanitizeIngredientForSave,
} from "../recipes/recipeLogic";
import { formatMacroValue } from "../shared/nutritionFormatters";
import { buildNutritionPlanDuplicateDisplayName } from "./nutritionPlanNameUtils";
import {
  validatePlanDraftLimits,
} from "../nutritionLimits";

export const PLAN_GOAL_MODE_GRAMS = "grams";
export const PLAN_GOAL_MODE_PERCENT = "percent";
export const PLAN_GOAL_IDS = ["calories", "protein", "carbs", "fats"];
export const PLAN_PERCENTAGE_GOAL_IDS = ["protein", "carbs", "fats"];
export const DEFAULT_PLAN_PERCENTAGE_DISTRIBUTION = {
  protein: 20,
  carbs: 55,
  fats: 25,
};
export const PLAN_MEAL_NAME_PRESET_IDS = [
  "breakfast-string",
  "lunch-string",
  "dinner-string",
  "snack-string",
];
export const PLAN_MEAL_NAME_PRESETS = [
  "Breakfast",
  "Lunch",
  "Dinner",
  "Snack",
];
const PLAN_MEAL_NAME_PRESET_LABELS = {
  "breakfast-string": {
    en: "Breakfast",
    es: "Desayuno",
  },
  "lunch-string": {
    en: "Lunch",
    es: "Comida",
  },
  "dinner-string": {
    en: "Dinner",
    es: "Cena",
  },
  "snack-string": {
    en: "Snack",
    es: "Snack",
  },
};

const PLAN_MACRO_CALORIES_PER_GRAM = {
  protein: 4,
  carbs: 4,
  fats: 9,
};

function getStoredCalorieTarget(targets) {
  return getSafeNumber(targets?.calories);
}

function getResolvedGramModeCalories(targets) {
  const storedCalories = getStoredCalorieTarget(targets);
  if (storedCalories > 0) {
    return storedCalories;
  }

  return getMacroCaloriesFromTargets(targets);
}

function getMacroCaloriesFromTargets(targets) {
  return PLAN_PERCENTAGE_GOAL_IDS.reduce(
    (total, id) =>
      total + getSafeNumber(targets?.[id]) * PLAN_MACRO_CALORIES_PER_GRAM[id],
    0,
  );
}

function inferMacroGoalMode(rawMode, nutrientsMap, percentagesMap) {
  const trimmedMode = String(rawMode ?? "").trim();
  if (trimmedMode === PLAN_GOAL_MODE_PERCENT || trimmedMode === PLAN_GOAL_MODE_GRAMS) {
    return trimmedMode;
  }

  if (Object.keys(percentagesMap).length > 0) {
    return PLAN_GOAL_MODE_PERCENT;
  }

  if (getMacroCaloriesFromTargets(nutrientsMap) > 0) {
    return getSafeNumber(nutrientsMap.calories) > 0
      ? PLAN_GOAL_MODE_PERCENT
      : PLAN_GOAL_MODE_GRAMS;
  }

  return PLAN_GOAL_MODE_PERCENT;
}

function buildGoalEntryMap(entries) {
  if (Array.isArray(entries)) {
    return entries.reduce((map, entry) => {
      const id = String(entry?.id ?? "").trim();
      if (!id) {
        return map;
      }

      map[id] = getSafeNumber(entry?.value);
      return map;
    }, {});
  }

  if (entries && typeof entries === "object") {
    return Object.keys(entries).reduce((map, id) => {
      map[id] = getSafeNumber(entries[id]);
      return map;
    }, {});
  }

  return {};
}

function formatGoalInputValue(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  if (numericValue >= 100 || Number.isInteger(numericValue)) {
    return String(Math.round(numericValue));
  }

  return String(Number(numericValue.toFixed(1)));
}

function formatWholeGoalInputValue(value, { allowZero = false } = {}) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  const roundedValue = Math.max(0, Math.round(numericValue));
  if (roundedValue === 0) {
    return allowZero ? "0" : "";
  }

  return String(roundedValue);
}

function normalizeWholeNumberInput(value) {
  const normalizedValue = normalizeDecimalInput(value).trim();

  if (normalizedValue === "") {
    return "";
  }

  const [integerPart = ""] = normalizedValue.split(".");

  return integerPart;
}

function clampPercentageValue(value) {
  return Math.max(0, getSafeNumber(value));
}

function getPercentageTotal(percentages) {
  return PLAN_PERCENTAGE_GOAL_IDS.reduce(
    (total, id) => total + clampPercentageValue(percentages?.[id]),
    0,
  );
}

function normalizePercentageDistribution(percentages) {
  const total = getPercentageTotal(percentages);

  if (total <= 0) {
    return PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id) => {
      map[id] = 0;
      return map;
    }, {});
  }

  let used = 0;
  return PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id, index) => {
    if (index === PLAN_PERCENTAGE_GOAL_IDS.length - 1) {
      map[id] = Number(Math.max(0, 100 - used).toFixed(1));
      return map;
    }

    const scaledValue = (clampPercentageValue(percentages?.[id]) / total) * 100;
    const roundedValue = Number(Math.max(0, scaledValue).toFixed(1));
    map[id] = roundedValue;
    used = Number((used + roundedValue).toFixed(1));
    return map;
  }, {});
}

function formatPercentageDraft(percentages) {
  return PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id) => {
    map[id] = formatGoalInputValue(percentages?.[id]);
    return map;
  }, {});
}

function buildPercentagesFromTargets(calories, targets) {
  const safeCalories = getSafeNumber(calories);
  if (safeCalories <= 0) {
    return {
      protein: 0,
      carbs: 0,
      fats: 0,
    };
  }

  return PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id) => {
    map[id] =
      ((getSafeNumber(targets[id]) * PLAN_MACRO_CALORIES_PER_GRAM[id]) / safeCalories) *
      100;
    return map;
  }, {});
}

export function buildTargetsFromPercentages(calories, percentages) {
  const safeCalories = getSafeNumber(calories);
  return PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id) => {
    const percentage = clampPercentageValue(percentages?.[id]);
    const caloriesForMacro = safeCalories * (percentage / 100);
    map[id] =
      safeCalories > 0 ? caloriesForMacro / PLAN_MACRO_CALORIES_PER_GRAM[id] : 0;
    return map;
  }, {});
}

export function normalizeGoalDraft(goals) {
  const nutrientsMap = buildGoalEntryMap(goals?.nutrients);
  const savedPercentagesMap = buildGoalEntryMap(goals?.percentages);
  const macroInputMode = inferMacroGoalMode(
    goals?.macro_input_mode,
    nutrientsMap,
    savedPercentagesMap,
  );
  const calorieTarget =
    macroInputMode === PLAN_GOAL_MODE_GRAMS
      ? getResolvedGramModeCalories(nutrientsMap)
      : getSafeNumber(nutrientsMap.calories);
  const fallbackPercentages =
    Object.keys(savedPercentagesMap).length > 0
      ? PLAN_PERCENTAGE_GOAL_IDS.reduce((map, id) => {
          map[id] = clampPercentageValue(savedPercentagesMap[id]);
          return map;
        }, {})
      : buildPercentagesFromTargets(calorieTarget, nutrientsMap);

  return {
    macro_input_mode: macroInputMode,
    nutrients: {
      calories: formatGoalInputValue(calorieTarget),
      protein: formatGoalInputValue(nutrientsMap.protein),
      carbs: formatGoalInputValue(nutrientsMap.carbs),
      fats: formatGoalInputValue(nutrientsMap.fats),
    },
    percentages: {
      protein: formatGoalInputValue(fallbackPercentages.protein),
      carbs: formatGoalInputValue(fallbackPercentages.carbs),
      fats: formatGoalInputValue(fallbackPercentages.fats),
    },
  };
}

export function getMacroGoalPercentages(goals, options = {}) {
  const normalizedGoals = normalizeGoalDraft(goals);
  const normalizedPercentages = normalizePercentageDistribution(
    normalizedGoals.percentages,
  );

  if (
    options?.fallbackToDefault === true &&
    getPercentageTotal(normalizedPercentages) <= 0
  ) {
    return { ...DEFAULT_PLAN_PERCENTAGE_DISTRIBUTION };
  }

  return normalizedPercentages;
}

function buildOptionRuntimeItems(option) {
  const optionItems = Array.isArray(option?.items) ? option.items : [];

  return optionItems.flatMap((item) => {
    if (isPersistedPlanOptionItem(item)) {
      return inflateNutritionRecipeRecord({
        id: option?.id ?? uuidv4(),
        items: [item],
      }).ingredients.map(cloneIngredient);
    }

    return [cloneIngredient(item)];
  });
}

function hasOwnValue(source, key) {
  return Object.prototype.hasOwnProperty.call(source ?? {}, key);
}

function hasRuntimeEquivalences(item) {
  return Array.isArray(item?.equivalences) && item.equivalences.length > 0;
}

function isPersistedPlanOptionItem(item) {
  if (!item || typeof item !== "object") {
    return false;
  }

  if (item.type === "equivalence") {
    return Array.isArray(item.options);
  }

  if (item.type !== "food" || hasRuntimeEquivalences(item)) {
    return false;
  }

  return (
    hasOwnValue(item, "quantity_g") ||
    Array.isArray(item.nutrients_per_100g) ||
    Array.isArray(item.common_measures) ||
    hasOwnValue(item, "selected_measure_id") ||
    (item.origin && typeof item.origin === "object")
  );
}

function buildPlanOptionItems(option) {
  return getValidOptionItems(option?.items).reduce((items, item, itemIndex) => {
    if (Array.isArray(item?.equivalences) && item.equivalences.length > 0) {
      const equivalenceItem = buildNutritionEquivalenceItemSnapshot(
        {
          id:
            item?.equivalenceGroupId ??
            item?.equivalence_group_id ??
            `${item?.id ?? "item"}-equivalence-${itemIndex}`,
          name: item?.equivalenceName ?? item?.name,
          options: [item, ...item.equivalences],
          origin: item?.equivalenceOrigin ?? item?.equivalence_origin,
        },
        itemIndex,
      );

      if (equivalenceItem) {
        items.push(equivalenceItem);
      }

      return items;
    }

    const foodItem = buildNutritionFoodItemSnapshot(item, {
      fallbackId: `${option?.id ?? "option"}-item-${itemIndex}`,
    });

    if (foodItem) {
      items.push(foodItem);
    }

    return items;
  }, []);
}

export function buildMealOptionItemFromSelectedFood(food, language, t) {
  return buildIngredientFromSelectedFood(food, language, t);
}

export function buildMealOptionItemsFromEquivalent(equivalentLike) {
  const nextItem = buildIngredientFromEquivalentSelection(equivalentLike);
  return nextItem ? [nextItem] : [];
}

export function buildMealOptionItemsFromRecipe(recipeLike) {
  return buildIngredientsFromRecipeSelection(recipeLike);
}

export function createEmptyMealOption(order = 0, options = {}) {
  return {
    id: uuidv4(),
    name: getDefaultOptionName(order, options?.language),
    items: [],
    persistIfEmpty: options?.persistIfEmpty === true,
    isStarterScaffold: options?.isStarterScaffold === true,
  };
}

export function createAddedMealOption(order = 0, options = {}) {
  return {
    ...createEmptyMealOption(order, {
      language: options?.language,
    }),
    persistIfEmpty: true,
  };
}

export function createEmptyMeal(order = 0, existingMeals = [], options = {}) {
  return {
    id: uuidv4(),
    name: getNextDefaultMealName(existingMeals, options),
    notes: "",
    order,
    isStarterScaffold: options?.isStarterScaffold === true,
    options: [
      createEmptyMealOption(0, {
        language: options?.language,
        persistIfEmpty: options?.persistInitialOption === true,
        isStarterScaffold: options?.isStarterScaffold === true,
      }),
    ],
  };
}

export function shouldPersistMealOptionWhenEmpty(option = {}) {
  return option?.persistIfEmpty === true || option?.isStarterScaffold !== true;
}

export function markMealOptionPersistable(option = {}) {
  if (option?.persistIfEmpty === true) {
    return option;
  }

  return {
    ...option,
    persistIfEmpty: true,
  };
}

function isSpanishLanguage(language = "en") {
  return String(language ?? "").toLowerCase().startsWith("es");
}

function resolveNutritionPlanLanguage(language = null) {
  const normalizedLanguage = String(language ?? "").trim();
  if (normalizedLanguage) {
    return normalizedLanguage;
  }

  return i18n?.resolvedLanguage || i18n?.language || "en";
}

function getPlanMealPresetLabelsForLanguage(language = "en") {
  return isSpanishLanguage(language)
    ? {
        "breakfast-string": "Desayuno",
        "lunch-string": "Comida",
        "dinner-string": "Cena",
        "snack-string": "Snack",
      }
    : {
        "breakfast-string": "Breakfast",
        "lunch-string": "Lunch",
        "dinner-string": "Dinner",
        "snack-string": "Snack",
      };
}

export function getLocalizedPlanMealName(presetId, language = "en", t = null) {
  const fallbackLabel =
    getPlanMealPresetLabelsForLanguage(language)?.[presetId] ??
    PLAN_MEAL_NAME_PRESET_LABELS[presetId]?.en ??
    "";

  if (typeof t !== "function") {
    return fallbackLabel;
  }

  return t(presetId, fallbackLabel);
}

export function getLocalizedPlanMealNamePresets(language = "en", t = null) {
  return PLAN_MEAL_NAME_PRESET_IDS.map((presetId) =>
    getLocalizedPlanMealName(presetId, language, t),
  );
}

export function getLocalizedPlanMealNamePresetItems(language = "en", t = null) {
  return PLAN_MEAL_NAME_PRESET_IDS.map((presetId) => ({
    id: presetId,
    label: getLocalizedPlanMealName(presetId, language, t),
    keywords: Object.values(PLAN_MEAL_NAME_PRESET_LABELS[presetId] ?? {}),
  }));
}

function getPlanMealNamePresetAliases(presetId, language = "en", t = null) {
  const fallbackLabels = PLAN_MEAL_NAME_PRESET_LABELS[presetId] ?? {};
  const aliases = new Set(Object.values(fallbackLabels).filter(Boolean));
  const localizedLabel = getLocalizedPlanMealName(presetId, language, t);
  if (localizedLabel) {
    aliases.add(localizedLabel);
  }

  return Array.from(aliases);
}

function resolveMealPresetIds(valueOrOptions = null) {
  if (Array.isArray(valueOrOptions)) {
    return valueOrOptions;
  }

  if (valueOrOptions && typeof valueOrOptions === "object") {
    if (Array.isArray(valueOrOptions.presetIds)) {
      return valueOrOptions.presetIds;
    }

    if (Array.isArray(valueOrOptions.presets)) {
      return valueOrOptions.presets;
    }
  }

  return PLAN_MEAL_NAME_PRESET_IDS;
}

function resolveMealPresetLanguage(valueOrOptions = null) {
  if (valueOrOptions && typeof valueOrOptions === "object") {
    return resolveNutritionPlanLanguage(valueOrOptions.language);
  }

  return resolveNutritionPlanLanguage();
}

function resolveMealPresetTranslator(valueOrOptions = null) {
  if (valueOrOptions && typeof valueOrOptions === "object") {
    return valueOrOptions.t ?? null;
  }

  return null;
}

function resolveMealPresetLabel(valueOrOptions = null, presetId, fallbackIndex = 0) {
  const presetIds = resolveMealPresetIds(valueOrOptions);
  if (!PLAN_MEAL_NAME_PRESET_LABELS[presetId]) {
    return presetIds[fallbackIndex] ?? "";
  }

  return getLocalizedPlanMealName(
    presetId,
    resolveMealPresetLanguage(valueOrOptions),
    resolveMealPresetTranslator(valueOrOptions),
  );
}

function getDefaultInitialMealNames(valueOrOptions = null) {
  return PLAN_MEAL_NAME_PRESET_IDS.slice(0, 3).map((presetId, index) =>
    resolveMealPresetLabel(valueOrOptions, presetId, index),
  );
}

export function normalizeMealNameForMatch(value) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function resolvePlanMealNamePresetKey(value, options = {}) {
  const normalizedValue = normalizeMealNameForMatch(value);
  if (!normalizedValue) {
    return null;
  }

  return (
    PLAN_MEAL_NAME_PRESET_IDS.find((presetId) =>
      getPlanMealNamePresetAliases(presetId, options.language, options.t).some(
        (alias) => normalizeMealNameForMatch(alias) === normalizedValue,
      ),
    ) ?? null
  );
}

export function getNextDefaultMealName(
  existingMeals = [],
  options = {},
) {
  const usedPresetNames = new Set(
    (Array.isArray(existingMeals) ? existingMeals : [])
      .map((meal) => resolvePlanMealNamePresetKey(meal?.name, options))
      .filter(Boolean),
  );
  const availablePresetId = PLAN_MEAL_NAME_PRESET_IDS.find(
    (presetId) => !usedPresetNames.has(presetId),
  );
  const fallbackPresetId =
    PLAN_MEAL_NAME_PRESET_IDS[PLAN_MEAL_NAME_PRESET_IDS.length - 1] ??
    "snack-string";

  return resolveMealPresetLabel(
    options,
    availablePresetId ?? fallbackPresetId,
    PLAN_MEAL_NAME_PRESET_IDS.length - 1,
  );
}

export function shouldShowCustomMealNameOption(
  query,
  options = {},
) {
  const committedQuery = commitMealNameValue(query);
  if (!committedQuery) {
    return false;
  }

  return resolvePlanMealNamePresetKey(committedQuery, options) == null;
}

export function commitMealNameValue(value, fallbackValue = "") {
  const trimmedValue = String(value ?? "").trim();
  if (trimmedValue) {
    return trimmedValue;
  }

  return String(fallbackValue ?? "").trim();
}

export function createDefaultInitialMeals(options = {}) {
  return getDefaultInitialMealNames(options).map((name, order) => ({
    ...createEmptyMeal(order, [], {
      ...options,
      isStarterScaffold: true,
    }),
    name,
  }));
}

export function markPlanMealsEngaged(draft = {}) {
  return {
    ...draft,
    mealsEngaged: true,
  };
}

function resolvePlanMealsEngaged(plan = {}) {
  return plan?.mealsEngaged === true || plan?.meals_engaged === true;
}

function isDefaultMealNameAlias(value, mealIndex) {
  const presetId = PLAN_MEAL_NAME_PRESET_IDS[mealIndex] ?? null;
  if (!presetId) {
    return normalizeMealNameForMatch(value) === normalizeMealNameForMatch(`Meal ${mealIndex + 1}`);
  }

  return resolvePlanMealNamePresetKey(value) === presetId;
}

function getDefaultMealName(mealIndex) {
  return PLAN_MEAL_NAME_PRESET_LABELS[PLAN_MEAL_NAME_PRESET_IDS[mealIndex]]?.en ??
    `Meal ${mealIndex + 1}`;
}

export function getDefaultOptionName(optionIndex, language = null) {
  return isSpanishLanguage(resolveNutritionPlanLanguage(language))
    ? `Opción ${optionIndex + 1}`
    : `Option ${optionIndex + 1}`;
}

export function isDefaultOptionNameAlias(value, optionIndex) {
  const normalizedValue = normalizeMealNameForMatch(value);
  if (!normalizedValue) {
    return false;
  }

  return ["en", "es"].some(
    (language) =>
      normalizeMealNameForMatch(getDefaultOptionName(optionIndex, language)) ===
      normalizedValue,
  );
}

export function cloneMealOption(option = {}, optionIndex = 0, options = {}) {
  return {
    id: String(option?.id ?? uuidv4()),
    name: String(
      option?.name ?? getDefaultOptionName(optionIndex, options?.language),
    ),
    items: buildOptionRuntimeItems(option),
    persistIfEmpty: option?.persistIfEmpty === true,
    isStarterScaffold: option?.isStarterScaffold === true,
  };
}

export function cloneMeal(meal = {}, mealIndex = 0) {
  return {
    id: String(meal?.id ?? uuidv4()),
    name: String(meal?.name ?? `Meal ${mealIndex + 1}`),
    notes: String(meal?.notes ?? ""),
    order: Number(meal?.order ?? mealIndex),
    isStarterScaffold: meal?.isStarterScaffold === true,
    options: (Array.isArray(meal?.options) ? meal.options : []).map((option, optionIndex) =>
      cloneMealOption(option, optionIndex),
    ),
  };
}

function cloneMealOptionPreservingRuntimeItems(option = {}, optionIndex = 0) {
  return {
    ...cloneMealOption(
      {
        ...option,
        items: [],
      },
      optionIndex,
    ),
    items: (Array.isArray(option?.items) ? option.items : []).map(cloneIngredient),
  };
}

function cloneMealPreservingRuntimeItems(meal = {}, mealIndex = 0) {
  return {
    ...cloneMeal(
      {
        ...meal,
        options: [],
      },
      mealIndex,
    ),
    options: (Array.isArray(meal?.options) ? meal.options : []).map(
      (option, optionIndex) =>
        cloneMealOptionPreservingRuntimeItems(option, optionIndex),
    ),
  };
}

function cloneMealWithOptions(meal = {}, nextOptions = [], mealIndex = 0) {
  return {
    id: String(meal?.id ?? uuidv4()),
    name: String(meal?.name ?? `Meal ${mealIndex + 1}`),
    notes: String(meal?.notes ?? ""),
    order: Number(meal?.order ?? mealIndex),
    isStarterScaffold: meal?.isStarterScaffold === true,
    options: Array.isArray(nextOptions) ? nextOptions : [],
  };
}

export function createInitialPlanDraft(plan = {}, options = {}) {
  const resolvedLanguage = resolveNutritionPlanLanguage(options.language);
  const mealsEngaged = resolvePlanMealsEngaged(plan);
  const shouldSeedStarterMeals = options.seedStarterMeals !== false && mealsEngaged !== true;
  let meals = (Array.isArray(plan?.meals) ? plan.meals : []).map((meal, mealIndex) => ({
    id: String(meal?.id ?? uuidv4()),
    name: String(meal?.name ?? ""),
    notes: String(meal?.notes ?? ""),
    order: Number(meal?.order ?? mealIndex),
    isStarterScaffold: meal?.isStarterScaffold === true,
    options: (Array.isArray(meal?.options) ? meal.options : []).map((option, optionIndex) => ({
      id: String(option?.id ?? uuidv4()),
      name: String(
        option?.name ?? getDefaultOptionName(optionIndex, resolvedLanguage),
      ),
      items: buildOptionRuntimeItems(option),
      persistIfEmpty: shouldPersistMealOptionWhenEmpty(option),
      isStarterScaffold: option?.isStarterScaffold === true,
    })),
  }));

  if (meals.length === 0 && shouldSeedStarterMeals) {
    meals = createDefaultInitialMeals({
      language: resolvedLanguage,
      t: options.t ?? null,
    });
  }

  return {
    id: String(plan?.id ?? uuidv4()),
    name: String(plan?.name ?? ""),
    notes: String(plan?.notes ?? ""),
    version: Math.max(1, Number(plan?.version ?? 1) || 1),
    _rev: getSafeNumber(plan?._rev),
    goals: normalizeGoalDraft(plan?.goals),
    mealsEngaged,
    meals,
  };
}

export function buildNutritionPlanDuplicateDraft(
  draft = {},
  { language = "en", namingSources = [] } = {},
) {
  return {
    name: buildNutritionPlanDuplicateDisplayName([draft, ...namingSources], language),
    notes: String(draft?.notes ?? ""),
    version: Math.max(1, Number(draft?.version ?? 1) || 1),
    goals: normalizeGoalDraft(draft?.goals),
    mealsEngaged: draft?.mealsEngaged === true,
    meals: (Array.isArray(draft?.meals) ? draft.meals : []).map((meal, mealIndex) =>
      cloneMealPreservingRuntimeItems(meal, mealIndex),
    ),
  };
}

export function getGoalTargets(goals) {
  const normalizedGoals = normalizeGoalDraft(goals);
  const calories =
    normalizedGoals.macro_input_mode === PLAN_GOAL_MODE_GRAMS
      ? getResolvedGramModeCalories(normalizedGoals.nutrients)
      : getSafeNumber(normalizedGoals.nutrients.calories);

  if (normalizedGoals.macro_input_mode === PLAN_GOAL_MODE_PERCENT) {
    return {
      calories,
      ...buildTargetsFromPercentages(calories, normalizedGoals.percentages),
    };
  }

  return {
    calories,
    protein: getSafeNumber(normalizedGoals.nutrients.protein),
    carbs: getSafeNumber(normalizedGoals.nutrients.carbs),
    fats: getSafeNumber(normalizedGoals.nutrients.fats),
  };
}

export function setGoalInput(goals, field, value) {
  const normalizedValue = normalizeDecimalInput(value);

  if (field === "calories") {
    return {
      ...goals,
      nutrients: {
        ...goals.nutrients,
        calories: normalizedValue,
      },
    };
  }

  if (goals.macro_input_mode === PLAN_GOAL_MODE_PERCENT) {
    return {
      ...goals,
      percentages: {
        ...goals.percentages,
        [field]: normalizedValue,
      },
    };
  }

  return {
    ...goals,
    nutrients: {
      ...goals.nutrients,
      [field]: normalizedValue,
    },
  };
}

export function setGoalCalories(goals, value) {
  const normalizedGoals = normalizeGoalDraft(goals);
  const normalizedValue = normalizeDecimalInput(value);
  const nextPercentages = getMacroGoalPercentages(normalizedGoals, {
    fallbackToDefault: true,
  });

  return {
    ...normalizedGoals,
    macro_input_mode: PLAN_GOAL_MODE_PERCENT,
    nutrients: {
      ...normalizedGoals.nutrients,
      calories: normalizedValue,
    },
    percentages: formatPercentageDraft(nextPercentages),
  };
}

export function setGoalPercentages(goals, percentages) {
  const normalizedGoals = normalizeGoalDraft(goals);
  const normalizedPercentages = normalizePercentageDistribution(percentages);

  return {
    ...normalizedGoals,
    macro_input_mode: PLAN_GOAL_MODE_PERCENT,
    percentages: formatPercentageDraft(normalizedPercentages),
  };
}

export function rebalanceMacroPercentages(percentages, lockedField, nextValue) {
  if (!PLAN_PERCENTAGE_GOAL_IDS.includes(lockedField)) {
    return normalizePercentageDistribution(percentages);
  }

  const baseDistribution =
    getPercentageTotal(percentages) > 0
      ? normalizePercentageDistribution(percentages)
      : { ...DEFAULT_PLAN_PERCENTAGE_DISTRIBUTION };
  const clampedLockedValue = Math.min(100, clampPercentageValue(nextValue));
  const remainingIds = PLAN_PERCENTAGE_GOAL_IDS.filter((id) => id !== lockedField);
  const remainingTarget = Math.max(0, 100 - clampedLockedValue);
  const remainingCurrentTotal = remainingIds.reduce(
    (total, id) => total + clampPercentageValue(baseDistribution[id]),
    0,
  );

  const nextDistribution = {
    [lockedField]: clampedLockedValue,
  };

  let used = clampedLockedValue;
  remainingIds.forEach((id, index) => {
    if (index === remainingIds.length - 1) {
      nextDistribution[id] = Number(Math.max(0, 100 - used).toFixed(1));
      return;
    }

    const scaledValue =
      remainingCurrentTotal > 0
        ? (clampPercentageValue(baseDistribution[id]) / remainingCurrentTotal) *
          remainingTarget
        : remainingTarget / remainingIds.length;
    const roundedValue = Number(Math.max(0, scaledValue).toFixed(1));
    nextDistribution[id] = roundedValue;
    used = Number((used + roundedValue).toFixed(1));
  });

  return normalizePercentageDistribution(nextDistribution);
}

export function setMacroGoalGrams(goals, field, value) {
  if (!PLAN_PERCENTAGE_GOAL_IDS.includes(field)) {
    return normalizeGoalDraft(goals);
  }

  const normalizedGoals = normalizeGoalDraft(goals);
  const nextNutrients = {
    ...normalizedGoals.nutrients,
    [field]: normalizeWholeNumberInput(value),
  };
  const nextCalories = getMacroCaloriesFromTargets(nextNutrients);

  return {
    ...normalizedGoals,
    macro_input_mode: PLAN_GOAL_MODE_GRAMS,
    nutrients: {
      ...nextNutrients,
      calories: formatGoalInputValue(nextCalories),
    },
    percentages: formatPercentageDraft(
      buildPercentagesFromTargets(nextCalories, nextNutrients),
    ),
  };
}

export function setGoalMode(goals, nextMode) {
  const normalizedGoals = normalizeGoalDraft(goals);
  if (nextMode !== PLAN_GOAL_MODE_PERCENT && nextMode !== PLAN_GOAL_MODE_GRAMS) {
    return normalizedGoals;
  }

  if (nextMode === normalizedGoals.macro_input_mode) {
    return normalizedGoals;
  }

  if (nextMode === PLAN_GOAL_MODE_PERCENT) {
    const targets = getGoalTargets(normalizedGoals);
    const percentages = buildPercentagesFromTargets(targets.calories, targets);

    return {
      ...normalizedGoals,
      macro_input_mode: PLAN_GOAL_MODE_PERCENT,
      percentages: {
        protein: formatGoalInputValue(percentages.protein),
        carbs: formatGoalInputValue(percentages.carbs),
        fats: formatGoalInputValue(percentages.fats),
      },
    };
  }

  const targets = getGoalTargets(normalizedGoals);
  const hasDefinedTargets =
    getSafeNumber(normalizedGoals.nutrients.calories) > 0 ||
    PLAN_PERCENTAGE_GOAL_IDS.some((id) => getSafeNumber(targets[id]) > 0);
  const roundedMacroTargets = {
    protein: Math.max(0, Math.round(targets.protein)),
    carbs: Math.max(0, Math.round(targets.carbs)),
    fats: Math.max(0, Math.round(targets.fats)),
  };
  const preservedCaloriesInput =
    String(normalizedGoals.nutrients.calories ?? "").trim() ||
    formatGoalInputValue(getMacroCaloriesFromTargets(roundedMacroTargets));
  return {
    ...normalizedGoals,
    macro_input_mode: PLAN_GOAL_MODE_GRAMS,
    nutrients: {
      calories: preservedCaloriesInput,
      protein: formatWholeGoalInputValue(roundedMacroTargets.protein, {
        allowZero: hasDefinedTargets,
      }),
      carbs: formatWholeGoalInputValue(roundedMacroTargets.carbs, {
        allowZero: hasDefinedTargets,
      }),
      fats: formatWholeGoalInputValue(roundedMacroTargets.fats, {
        allowZero: hasDefinedTargets,
      }),
    },
    percentages: formatPercentageDraft(
      buildPercentagesFromTargets(getSafeNumber(preservedCaloriesInput), roundedMacroTargets),
    ),
  };
}

export function hasDefinedGoalTargets(goals) {
  const targets = getGoalTargets(goals);

  return PLAN_GOAL_IDS.some((id) => getSafeNumber(targets?.[id]) > 0);
}

export function getMealOptionTotals(option) {
  return getRecipeTotals(option?.items);
}

function getSelectedMealOptionId(selectedOptionIdsByMeal, mealId) {
  if (selectedOptionIdsByMeal instanceof Map) {
    return String(selectedOptionIdsByMeal.get(mealId) ?? "").trim();
  }

  if (selectedOptionIdsByMeal && typeof selectedOptionIdsByMeal === "object") {
    return String(selectedOptionIdsByMeal[mealId] ?? "").trim();
  }

  return "";
}

function getSelectedMealOption(meal, selectedOptionIdsByMeal) {
  const mealOptions = Array.isArray(meal?.options) ? meal.options : [];
  if (mealOptions.length === 0) {
    return null;
  }

  const selectedOptionId = getSelectedMealOptionId(selectedOptionIdsByMeal, meal?.id);
  if (!selectedOptionId) {
    return mealOptions[0];
  }

  return (
    mealOptions.find((option) => option?.id === selectedOptionId) ?? mealOptions[0]
  );
}

export function getPlanDraftTotals(draft, selectedOptionIdsByMeal = null) {
  return (Array.isArray(draft?.meals) ? draft.meals : []).reduce(
    (totals, meal) => {
      const selectedOption = getSelectedMealOption(meal, selectedOptionIdsByMeal);
      if (!selectedOption) {
        return totals;
      }

      const optionTotals = getMealOptionTotals(selectedOption);
      totals.calories += optionTotals.calories;
      totals.protein += optionTotals.protein;
      totals.carbs += optionTotals.carbs;
      totals.fats += optionTotals.fats;
      totals.fiber += optionTotals.fiber;

      Object.keys(totals.micros).forEach((key) => {
        totals.micros[key] += getSafeNumber(optionTotals.micros?.[key]);
      });

      return totals;
    },
    getRecipeTotals([]),
  );
}

export function getObjectiveProgress(currentValue, targetValue) {
  const current = getSafeNumber(currentValue);
  const target = getSafeNumber(targetValue);

  if (target <= 0) {
    return {
      current,
      target,
      progress: 0,
      completionLabel: "0",
    };
  }

  const progress = Math.min((current / target) * 100, 100);
  return {
    current,
    target,
    progress,
    completionLabel: formatMacroValue((current / target) * 100),
  };
}

export function buildGoalsForSave(goals, options = {}) {
  const normalizedGoals = normalizeGoalDraft(goals);
  const targets = getGoalTargets(normalizedGoals);
  const nutrients = PLAN_GOAL_IDS.map((id) => ({
    id,
    value: getSafeNumber(targets[id]),
  })).filter((entry) => entry.value > 0);
  const percentages = PLAN_PERCENTAGE_GOAL_IDS.map((id) => ({
    id,
    value: getSafeNumber(normalizedGoals.percentages[id]),
  })).filter((entry) => entry.value > 0);

  if (nutrients.length === 0 && percentages.length === 0) {
    if (
      options?.preserveEmptyMode === true &&
      normalizedGoals.macro_input_mode === PLAN_GOAL_MODE_GRAMS
    ) {
      return {
        macro_input_mode: PLAN_GOAL_MODE_GRAMS,
        nutrients: [],
      };
    }

    return null;
  }

  const goalRecord = {
    macro_input_mode: normalizedGoals.macro_input_mode,
    nutrients,
  };

  if (percentages.length > 0) {
    goalRecord.percentages = percentages;
  }

  return goalRecord;
}

function getValidOptionItems(items) {
  return (Array.isArray(items) ? items : [])
    .map(sanitizeIngredientForSave)
    .filter(Boolean);
}

function buildPlanOptionForSave(option, optionIndex, options = {}) {
  const trimmedName = String(option?.name ?? "").trim();
  const fallbackName = getDefaultOptionName(optionIndex, options?.language);
  const items = buildPlanOptionItems(option);
  const hasMeaningfulName =
    trimmedName.length > 0 && !isDefaultOptionNameAlias(trimmedName, optionIndex);
  const shouldPersistEmptyOption =
    option?.persistIfEmpty === true ||
    (options?.retainStarterScaffold === true && option?.isStarterScaffold === true);

  if (items.length === 0 && !hasMeaningfulName && !shouldPersistEmptyOption) {
    return null;
  }

  return {
    id: option?.id ?? uuidv4(),
    name: trimmedName || fallbackName,
    items,
  };
}

function buildPlanMealForSave(meal, mealIndex, options = {}) {
  const trimmedName = String(meal?.name ?? "").trim();
  const trimmedNotes = String(meal?.notes ?? "").trim();
  const fallbackName = getDefaultMealName(mealIndex);
  const shouldRetainStarterScaffold =
    options?.retainStarterScaffold === true && meal?.isStarterScaffold === true;
  const persistedOptions = (Array.isArray(meal?.options) ? meal.options : [])
    .map((option, optionIndex) =>
      buildPlanOptionForSave(option, optionIndex, {
        language: options?.language,
        retainStarterScaffold: shouldRetainStarterScaffold,
      }),
    )
    .filter(Boolean);
  const hasMeaningfulName =
    trimmedName.length > 0 && !isDefaultMealNameAlias(trimmedName, mealIndex);

  if (
    persistedOptions.length === 0 &&
    trimmedNotes.length === 0 &&
    !hasMeaningfulName &&
    !shouldRetainStarterScaffold
  ) {
    return null;
  }

  return {
    id: meal.id,
    name: trimmedName || fallbackName,
    order: mealIndex,
    notes: trimmedNotes,
    options: persistedOptions,
  };
}

export function getPlanMealsForSave(draft) {
  const retainStarterScaffold = draft?.mealsEngaged === true;
  return (Array.isArray(draft?.meals) ? draft.meals : [])
    .map((meal, mealIndex) =>
      buildPlanMealForSave(meal, mealIndex, {
        retainStarterScaffold,
      }),
    )
    .filter(Boolean);
}

export function isPlanDraftSemanticallyEmpty(draft) {
  const notes = String(draft?.notes ?? "").trim();
  const goalsForSave = buildGoalsForSave(draft?.goals);
  const mealsForSave = getPlanMealsForSave(draft);
  const hasName = String(draft?.name ?? "").trim().length > 0;

  return !hasName && notes.length === 0 && goalsForSave == null && mealsForSave.length === 0;
}

export function hasPlanDraftPersistenceAnchor(draft) {
  const notes = String(draft?.notes ?? "").trim();
  const mealsForSave = getPlanMealsForSave(draft);
  const hasName = String(draft?.name ?? "").trim().length > 0;

  return hasName || notes.length > 0 || mealsForSave.length > 0;
}

export function getPlanDraftValidity(draft) {
  const meals = Array.isArray(draft?.meals) ? draft.meals : [];
  const mealsForSave = getPlanMealsForSave(draft);
  const notes = String(draft?.notes ?? "").trim();
  const goalsForSave = buildGoalsForSave(draft?.goals);
  const hasName = String(draft?.name ?? "").trim().length > 0;
  const limitsValidation = validatePlanDraftLimits({
    name: draft?.name,
    notes: draft?.notes,
    meals: mealsForSave,
  });
  const mealSummaries = meals.map((meal, mealIndex) => {
    const options = (Array.isArray(meal?.options) ? meal.options : []).map((option) => {
      const itemsForSave = getValidOptionItems(option?.items);
      return {
        id: option?.id,
        hasName: String(option?.name ?? "").trim().length > 0,
        itemsForSave,
        isValid: String(option?.name ?? "").trim().length > 0 && itemsForSave.length > 0,
      };
    });
    const persistedMeal = buildPlanMealForSave(meal, mealIndex, {
      retainStarterScaffold: draft?.mealsEngaged === true,
    });

    return {
      id: meal?.id,
      hasName: String(meal?.name ?? "").trim().length > 0,
      options,
      hasValidOption: options.some((option) => option.isValid),
      willPersist: persistedMeal != null,
      isValid:
        String(meal?.name ?? "").trim().length > 0 &&
        options.length > 0 &&
        options.every((option) => option.isValid),
    };
  });

  return {
    canSave:
      (hasName || notes.length > 0 || goalsForSave != null || mealsForSave.length > 0) &&
      limitsValidation.ok,
    mealSummaries,
    limitsValidation,
  };
}

export function reorderMeals(meals) {
  return (Array.isArray(meals) ? meals : []).map((meal, mealIndex) => ({
    ...cloneMealPreservingRuntimeItems(meal, mealIndex),
    order: mealIndex,
  }));
}

export function reorderMealOptions(meals, mealId, nextOptions) {
  return (Array.isArray(meals) ? meals : []).map((meal, mealIndex) => {
    if (meal?.id !== mealId) {
      return cloneMealPreservingRuntimeItems(meal, mealIndex);
    }

    return {
      ...cloneMealPreservingRuntimeItems(meal, mealIndex),
      options: (Array.isArray(nextOptions) ? nextOptions : []).map((option, optionIndex) =>
        cloneMealOptionPreservingRuntimeItems(option, optionIndex),
      ),
    };
  });
}

export function reorderMealOptionItems(meals, mealId, optionId, nextItems) {
  return (Array.isArray(meals) ? meals : []).map((meal, mealIndex) => {
    if (meal?.id !== mealId) {
      return cloneMealPreservingRuntimeItems(meal, mealIndex);
    }

    return {
      ...cloneMealPreservingRuntimeItems(meal, mealIndex),
      options: meal.options.map((option, optionIndex) => {
        if (option?.id !== optionId) {
          return cloneMealOptionPreservingRuntimeItems(option, optionIndex);
        }

        return {
          ...cloneMealOptionPreservingRuntimeItems(option, optionIndex),
          items: (Array.isArray(nextItems) ? nextItems : []).map((item) =>
            cloneIngredient(item),
          ),
        };
      }),
    };
  });
}

export function moveMealOptionItem(
  meals,
  {
    fromMealId,
    fromOptionId,
    itemId,
    toMealId,
    toOptionId,
    overItemId = null,
    insertAfterOverItem = false,
  } = {},
) {
  const currentMeals = Array.isArray(meals) ? meals : [];
  const normalizedFromMealId = String(fromMealId ?? "").trim();
  const normalizedFromOptionId = String(fromOptionId ?? "").trim();
  const normalizedItemId = String(itemId ?? "").trim();
  const normalizedToMealId = String(toMealId ?? "").trim();
  const normalizedToOptionId = String(toOptionId ?? "").trim();
  const normalizedOverItemId = String(overItemId ?? "").trim();

  if (
    !normalizedFromMealId ||
    !normalizedFromOptionId ||
    !normalizedItemId ||
    !normalizedToMealId ||
    !normalizedToOptionId
  ) {
    return currentMeals;
  }

  const sourceMealIndex = currentMeals.findIndex(
    (meal) => meal?.id === normalizedFromMealId,
  );
  const targetMealIndex = currentMeals.findIndex(
    (meal) => meal?.id === normalizedToMealId,
  );

  if (sourceMealIndex < 0 || targetMealIndex < 0) {
    return currentMeals;
  }

  const sourceMeal = currentMeals[sourceMealIndex];
  const targetMeal = currentMeals[targetMealIndex];
  const sourceOptionIndex = (Array.isArray(sourceMeal?.options) ? sourceMeal.options : []).findIndex(
    (option) => option?.id === normalizedFromOptionId,
  );
  const targetOptionIndex = (Array.isArray(targetMeal?.options) ? targetMeal.options : []).findIndex(
    (option) => option?.id === normalizedToOptionId,
  );

  if (sourceOptionIndex < 0 || targetOptionIndex < 0) {
    return currentMeals;
  }

  const sourceOption = sourceMeal.options[sourceOptionIndex];
  const targetOption = targetMeal.options[targetOptionIndex];
  const sourceItems = Array.isArray(sourceOption.items) ? sourceOption.items : [];
  const sourceIndex = sourceItems.findIndex((item) => item?.id === normalizedItemId);

  if (sourceIndex < 0) {
    return currentMeals;
  }

  if (
    normalizedFromMealId === normalizedToMealId &&
    normalizedFromOptionId === normalizedToOptionId &&
    normalizedOverItemId === normalizedItemId
  ) {
    return currentMeals;
  }

  const isSameContainer =
    normalizedFromMealId === normalizedToMealId &&
    normalizedFromOptionId === normalizedToOptionId;
  const nextSourceItems = sourceItems.map((item) => cloneIngredient(item));
  const [movedItem] = nextSourceItems.splice(sourceIndex, 1);
  if (!movedItem) {
    return currentMeals;
  }

  if (isSameContainer) {
    const sameContainerOverIndex = normalizedOverItemId
      ? nextSourceItems.findIndex((item) => item?.id === normalizedOverItemId)
      : -1;
    const insertIndex =
      sameContainerOverIndex < 0
        ? Math.min(sourceIndex, nextSourceItems.length)
        : sameContainerOverIndex + (insertAfterOverItem ? 1 : 0);

    nextSourceItems.splice(
      Math.max(0, Math.min(insertIndex, nextSourceItems.length)),
      0,
      movedItem,
    );
    const nextSourceOption = markMealOptionPersistable({
      ...cloneMealOptionPreservingRuntimeItems(sourceOption, sourceOptionIndex),
      items: nextSourceItems,
    });

    return currentMeals.map((meal, mealIndex) => {
      if (mealIndex !== sourceMealIndex) {
        return meal;
      }

      const nextOptions = meal.options.map((option, optionIndex) =>
        optionIndex === sourceOptionIndex ? nextSourceOption : option,
      );
      return cloneMealWithOptions(meal, nextOptions, mealIndex);
    });
  }

  const targetItems = Array.isArray(targetOption.items) ? targetOption.items : [];
  const nextTargetItems = targetItems.map((item) => cloneIngredient(item));
  const targetOverIndex = normalizedOverItemId
    ? nextTargetItems.findIndex((item) => item?.id === normalizedOverItemId)
    : -1;
  const targetInsertIndex =
    targetOverIndex < 0
      ? nextTargetItems.length
      : targetOverIndex + (insertAfterOverItem ? 1 : 0);

  nextTargetItems.splice(
    Math.max(0, Math.min(targetInsertIndex, nextTargetItems.length)),
    0,
    movedItem,
  );

  const nextSourceOption = markMealOptionPersistable({
    ...cloneMealOptionPreservingRuntimeItems(sourceOption, sourceOptionIndex),
    items: nextSourceItems,
  });
  const nextTargetOption = markMealOptionPersistable({
    ...cloneMealOptionPreservingRuntimeItems(targetOption, targetOptionIndex),
    items: nextTargetItems,
  });

  if (sourceMealIndex === targetMealIndex) {
    return currentMeals.map((meal, mealIndex) => {
      if (mealIndex !== sourceMealIndex) {
        return meal;
      }

      const nextOptions = meal.options.map((option, optionIndex) => {
        if (optionIndex === sourceOptionIndex) {
          return nextSourceOption;
        }
        if (optionIndex === targetOptionIndex) {
          return nextTargetOption;
        }
        return option;
      });
      return cloneMealWithOptions(meal, nextOptions, mealIndex);
    });
  }

  return currentMeals.map((meal, mealIndex) => {
    if (mealIndex === sourceMealIndex) {
      const nextOptions = meal.options.map((option, optionIndex) =>
        optionIndex === sourceOptionIndex ? nextSourceOption : option,
      );
      return cloneMealWithOptions(meal, nextOptions, mealIndex);
    }

    if (mealIndex === targetMealIndex) {
      const nextOptions = meal.options.map((option, optionIndex) =>
        optionIndex === targetOptionIndex ? nextTargetOption : option,
      );
      return cloneMealWithOptions(meal, nextOptions, mealIndex);
    }

    return meal;
  });
}

export function serializePlanDraftForSave(draft) {
  const draftValidity = getPlanDraftValidity(draft);
  if (!draftValidity.canSave) {
    return null;
  }

  const meals = getPlanMealsForSave(draft);

  const record = {
    id: draft.id,
    name: String(draft?.name ?? "").trim(),
    notes: String(draft?.notes ?? "").trim(),
    version: Math.max(1, Number(draft?.version ?? 1) || 1),
    _rev: getSafeNumber(draft?._rev, 0),
    goals: buildGoalsForSave(draft?.goals, { preserveEmptyMode: true }),
    meals,
  };

  if (draft?.mealsEngaged === true) {
    record.meals_engaged = true;
  }

  return record;
}
